/**
 * Web Push helpers — request permission, fetch VAPID key, register the
 * browser subscription, persist it to the backend.
 */
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i);
  return output;
}

export function pushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export function pushPermissionState() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) throw new Error("Service worker unsupported");
  // Wait for the worker to take control; falls back to navigator.serviceWorker.ready.
  const reg = await navigator.serviceWorker.ready;
  return reg;
}

export async function enableWebPush() {
  if (!pushSupported()) throw new Error("Web push is not supported on this device.");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notification permission was not granted.");

  // Fetch VAPID public key
  const { data } = await api.get("/push/vapid-public-key");
  if (!data?.public_key) throw new Error("Server has not configured web push.");

  const reg = await getRegistration();

  // Reuse the existing subscription on this browser if one already exists.
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // If the existing sub was created with a DIFFERENT VAPID key, refresh it.
    try { await sub.unsubscribe(); } catch { /* ignore */ }
    sub = null;
  }

  sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.public_key),
  });

  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    expiration_time: sub.expirationTime || null,
    user_agent: navigator.userAgent || "",
  });

  return { ok: true, endpoint: json.endpoint };
}

export async function disableWebPush() {
  if (!pushSupported()) return { ok: true };
  try {
    const reg = await getRegistration();
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      try { await sub.unsubscribe(); } catch { /* ignore */ }
      await api.post("/push/unsubscribe", { endpoint });
    }
  } catch { /* best effort */ }
  return { ok: true };
}

export async function fetchPushStatus() {
  try {
    const res = await api.get("/push/status");
    return res.data || { configured: false, subscriptions: 0 };
  } catch { return { configured: false, subscriptions: 0 }; }
}

export async function sendPushTest() {
  return api.post("/push/test").then((r) => r.data);
}
